import { Modal } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import loader from "assets/img/loader.gif";
import TextCarousel from "./TextCarousel";
import { ICSS } from "features/common/Interfaces";

interface IPlayer {
  SendMessage(
    gameManager: string,
    event: string,
    options?: number | string
  ): void;
}
declare global {
  interface Window {
    UnityLoader: {
      instantiate(htmlElementId: string, config: string): IPlayer;
    };
    loadingCompleted(): void;
    GetEnvironmentDataFromWebBrowser(): void;
    playContent(value: string): void;
    FullScreen(status: string): void;
    ContentFinished(): void;
    gameInstance: IPlayer;
  }
}

const customStyles: ICSS = {
  loader: {
    width: "64px",
    height: "64px",
    margin: "30px 0 15px",
  },
  loaderContent: {
    width: "450px",
    background: "rgb(251 251 251)",
    borderBottomLeftRadius: "17px",
    borderBottomRightRadius: "17px",
    height: "150px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    padding: "10px",
  },
  gameContainer: {
    width: "100vw",
    height: "100vh",
    zIndex: 9999,
    visibility: "hidden",
    position: "absolute",
    top: 0,
    left: 0,
  },
};
const envTypes = [
  "predev",
  "dev",
  "staging",
  "staging-2",
  "prod",
  "preprod",
  "localhost",
  "devops",
];
interface IUnityPlayerProps {
  id: string;
  show: boolean;
  onClose(): void;
}
export const s3Url = process.env.REACT_APP_WEBGL_URL;

function UnityPlayer({ id, show, onClose }: IUnityPlayerProps) {
  const { t, i18n } = useTranslation();
  // const [player, setPlayer] = useState<IPlayer>();
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState<string>();
  const texts = [
    t("download in progress"),
    t("downloading engine"),
    t("downloading content"),
    t("downloading data"),
  ];

  function IsFullScreenCurrently() {
    var full_screen_element = document.fullscreenElement || null;

    // If no element is in full-screen
    if (full_screen_element === null) return false;
    else return true;
  }

  function GoOutFullscreen() {
    if (IsFullScreenCurrently()) document.exitFullscreen();
  }

  function GoInFullscreen(element: HTMLElement) {
    element.requestFullscreen();
  }

  const loadingCompleted = () => {
    setLoading(false);
    setReady(true);
  };

  const GetEnvironmentDataFromWebBrowser = () => {
    try {
      const env = envTypes.indexOf(process.env.REACT_APP_ENV!);
      // if the environment is localhost then set it value manually
      const url = window.location.origin;
      window.gameInstance.SendMessage(
        "GameManager",
        "OnReceivingEnvironmentDataFromJS",
        url.indexOf("localhost") !== -1 ? 6 : env
      );
    } catch (error) {
      console.log(error);
      closeHandler();
    }
  };

  const playContent = () => {
    // nothing to do here...
  };

  const FullScreen = () => {
    if (IsFullScreenCurrently()) GoOutFullscreen();
    else GoInFullscreen(document.documentElement);
  };

  const ContentFinished = () => {
    GoOutFullscreen();
    setReady(false);
    onClose();
  };

  useEffect(() => {
    try {
      fetch(`${s3Url}Player_Config.json`)
        .then((response) => response.json())
        .then((result: { version: string }) => {
          setVersion(result.version);
        });
      window.loadingCompleted = loadingCompleted;
      window.GetEnvironmentDataFromWebBrowser =
        GetEnvironmentDataFromWebBrowser;
      window.playContent = playContent;
      window.FullScreen = FullScreen;
      window.ContentFinished = ContentFinished;

      setLoading(true);
    } catch (error) {
      console.log(error);
      closeHandler();
    }

    return () => {
      closeHandler();
    };
  }, []);

  useEffect(() => {
    if (version) {
      const unityJsonFilename = `${s3Url}Build/Abjadiyat_v5_Web_${version}.json`;
      window.gameInstance = window.UnityLoader.instantiate(
        "gameContainer",
        unityJsonFilename
      );
    }
  }, [version]);

  useEffect(() => {
    if (ready) {
      try {
        var PlayContentInfo = {
          _contentId: id,
          _fullScreenBttnStatus: true,
        };
        window.gameInstance.SendMessage(
          "WebGLPlayer",
          "PlayContent",
          JSON.stringify(PlayContentInfo)
        );
      } catch (error) {
        console.log(error);
        closeHandler();
      }
    }
    const playerContainer = document.getElementById(
      "gameContainer"
    ) as HTMLDivElement;

    playerContainer!.style.visibility = ready ? "visible" : "hidden";
  }, [ready]);

  const closeHandler = () => {
    try {
      setReady(false);
      const playerContainer = document.getElementById(
        "gameContainer"
      ) as HTMLDivElement;
      playerContainer!.style.visibility = "hidden";
      if (IsFullScreenCurrently()) document.exitFullscreen();
      onClose();
      window.gameInstance.SendMessage("WebGLPlayer", "CloseContent");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal
      open={show}
      sx={{
        display: "flex",
        justifyContent: "center",
        direction: i18n.dir(),
      }}
      onClose={closeHandler}
    >
      <div
        style={{
          ...customStyles.loaderContent,
          visibility: loading ? "visible" : "hidden",
        }}
      >
        <img src={loader} style={customStyles.loader} alt="loader" />
        <TextCarousel texts={texts} />
      </div>
    </Modal>
  );
}

export default UnityPlayer;
